import React, { useCallback, useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { navigate, useParams } from '@reach/router'
import { Button, Loader } from '@refera/ui-web'
import { Grid } from '@mui/material'

import HeaderTitle from '_/components/header-title'
import { Autocomplete, BasicInput, ControlledSwitch, MultilineInput } from '_/components/inputs'

import { ROUTES } from '_/utils/constants'
import { PERMISSIONS, PERMISSIONS_ACTIONS } from '_/utils/constants/permissions'

import useRolePermission from '_/hooks/use-role-permission'

import { getAgencies } from '_/modules/agency/actions'
import { getProfileList } from '_/modules/profile/actions'
import { getProfilesSelector } from '_/modules/profile/selectors'
import { getAgenciesListSelector } from '_/modules/agency/selectors'
import { getAreaOfActivityList } from '_/modules/areaOfActivity/actions'
import { areasOfActivityListSelector } from '_/modules/areaOfActivity/selectors'

import { formatErrorMessage } from '_/views/finance/utils/FormatErrorMessage'

import { useToast } from '_/hooks/use-toast'

import useStyles from './styles'
import {
  createWidget,
  deleteWidget,
  getWidgetDetails,
  updateWidget,
} from '_/modules/widgets/actions'
import { useDialog } from '_/hooks/use-dialog'

function mapPayloadToIds(items) {
  return items?.map(item => item?.id)
}

export function WidgetDetails() {
  const styles = useStyles()
  const dispatch = useDispatch()

  const { showToast } = useToast()
  const { showDialog, closeDialog } = useDialog()

  const { checkIfUserDoesNotPermission } = useRolePermission()

  const { widgetId } = useParams()

  const profilesList = useSelector(getProfilesSelector)
  const agenciesList = useSelector(getAgenciesListSelector)
  const areaOfActivityList = useSelector(areasOfActivityListSelector)

  const [loading, setLoading] = useState(true)
  const [widget, setWidget] = useState(null)
  const [hasPermissionToViewPage, setHasPermissionToViewPage] = useState(false)

  const methods = useForm({ mode: 'all' })

  const { handleSubmit, control, reset } = methods

  function showError(error) {
    showToast({
      type: 'error',
      message: formatErrorMessage(error),
    })
  }

  async function fetchWidgetDetails() {
    setLoading(false)
    await dispatch(getWidgetDetails(widgetId))
      .then(res => setWidget(res))
      .catch(err => {
        showError(err)
        setLoading(false)
        handleGoBack()
      })
  }

  async function fetchFormOptions() {
    setLoading(true)

    try {
      if (!profilesList?.results?.length) await dispatch(getProfileList())
      if (!areaOfActivityList?.length) await dispatch(getAreaOfActivityList())
      if (!agenciesList?.results?.length)
        await dispatch(getAgencies({ pageSize: 1000, orderBy: 'name' }))
    } catch (err) {
      console.error('Erro ao carregar opções do formulário:', err)
      showToast({
        type: 'error',
        message: 'Erro ao carregar opções do formulário. Tente novamente.',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleSubmitForm = useCallback(
    async data => {
      const { agencies, areaOfActivity, profiles } = data

      const payload = {
        ...data,
        agencies: mapPayloadToIds(agencies),
        areaOfActivity: mapPayloadToIds(areaOfActivity),
        activeProfile: mapPayloadToIds(profiles),
      }

      setLoading(true)
      const action = widgetId ? updateWidget(widgetId, payload) : createWidget(payload)
      await dispatch(action)
        .then(() => {
          showToast({ type: 'success' })
        })
        .catch(showError)
        .finally(() => setLoading(false))
    },
    [dispatch, showToast, widgetId]
  )

  async function handleDelete() {
    showDialog({
      type: 'error',
      subject: 'Excluir Widget',
      description: 'Você tem certeza que deseja executar esta ação?',
      onApprove: async () => {
        setLoading(true)
        await dispatch(deleteWidget(widgetId))
          .then(() => {
            closeDialog()
            showToast({ type: 'success' })
            handleGoBack()
          })
          .catch(err => {
            showError(err)
            setLoading(false)
          })
      },
      labelApprove: 'Sim',
      labelCancel: 'Não',
      onCancel: closeDialog,
    })
  }

  function handleGoBack() {
    navigate(ROUTES.MANAGE_WIDGETS)
  }

  function handleCancel() {
    showDialog({
      type: 'error',
      subject: 'Cancelar',
      description:
        'Você perderá todas as alterações realizadas neste registro. Tem certeza que deseja continuar?',
      onApprove: () => {
        closeDialog()
        handleGoBack()
      },
      labelApprove: 'Sim',
      labelCancel: 'Não',
      onCancel: closeDialog,
    })
  }

  function handleDefaultValues() {
    const defaultValues = {
      title: widget?.title || '',
      sql: widget?.sql || '',
      active: widget?.active !== undefined ? widget?.active : true,
      profiles: widget?.activeProfile || [],
      areaOfActivity: widget?.areaOfActivity || [],
      agencies: widget?.agencies || [],
    }

    reset(defaultValues)
    setLoading(false)
  }

  useEffect(() => {
    if (!hasPermissionToViewPage) return

    if (widgetId) {
      fetchWidgetDetails()
    }
    fetchFormOptions()
  }, [hasPermissionToViewPage])

  useEffect(() => {
    const shouldRedirectUser = checkIfUserDoesNotPermission(
      PERMISSIONS.WIDGETS,
      PERMISSIONS_ACTIONS.READ
    )

    if (shouldRedirectUser) {
      navigate(ROUTES.ACCESS_DENIED)
    } else {
      setHasPermissionToViewPage(true)
    }
  }, [])

  useEffect(() => {
    if (widget) handleDefaultValues()
  }, [widget])

  if (!hasPermissionToViewPage) {
    return <Loader hasBackdrop open />
  }

  return (
    <Grid>
      <Loader hasBackdrop open={loading} />
      <HeaderTitle title="Widgets" backButtonAction={handleGoBack}>
        <Grid className={styles.buttonsContainer}>
          {widgetId && (
            <Button variant="secondary" color="red" onClick={handleDelete}>
              Excluir
            </Button>
          )}
          <Button variant="secondary" color="red" onClick={handleCancel}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSubmit(handleSubmitForm)} disabled={loading}>
            Salvar
          </Button>
        </Grid>
      </HeaderTitle>
      <FormProvider {...methods}>
        <form id="widget-details" className={styles.container}>
          <Grid className={styles.row}>
            <BasicInput
              label="Nome"
              name="title"
              required
              containerClasses={styles.input}
              labelClasses={styles.label}
              rules={{
                required: { value: true, message: 'Esse campo é obrigatório' },
                maxLength: { value: 200, message: `Esse campo aceita no máximo 200 caracteres` },
              }}
            />
            <Autocomplete
              options={profilesList?.results || []}
              getOptionLabel={option => option?.desc}
              label="Perfis"
              name="profiles"
              multiple
              required
            />
            <Autocomplete
              options={areaOfActivityList || []}
              getOptionLabel={option => option?.name}
              label="Áreas de atuação"
              name="areaOfActivity"
              multiple
            />
            <Autocomplete
              options={agenciesList?.results || []}
              getOptionLabel={option => option?.name}
              label="Intermediárias"
              name="agencies"
              multiple
            />
          </Grid>
          <Grid className={styles.fullRow}>
            <MultilineInput defaultValue="" name="sql" label="SQL" minRows={30} required />
          </Grid>
          <Grid className={styles.row}>
            <ControlledSwitch name="active" label="Ativo" control={control} defaultValue />
          </Grid>
        </form>
      </FormProvider>
    </Grid>
  )
}
